import { StatusCode } from "api/protocols";
import { useTranslation } from "react-i18next";
import { UserLogin } from "api/rpc";
import { GetTeeTimesCustomer } from "api/rpc/2022-09/customer/teetime";
import { LockTeeTimeById } from "api/rpc/2022-09/guest/bookingEngine";
import { ButtonNew as Button } from "components/buttonNew";
import FormLayout from "components/form/FormLayout";
import Input from "components/form/input/index";
import { convertTime, formatDateFromString } from "helpers/Helpers";
import { useAppDispatch } from "hooks/redux";
import React, { useState } from "react";
import { useHistory } from "react-router";
import { showError } from "redux/actions/ui";
import { ITeeTime } from "redux/reducers/models/teetime";
import { useTeeOnPortalContext } from "../TeeOnPortalContext";
import "./teeOnPortalLogin.scss";
import { useLocation } from "react-router-dom";

interface ILoginState {
  email: string;
  password: string;
}

interface ILocationState {
  params: { golfCourses: boolean };
}

export default function TeeOnPortalLogin() {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { teeOnPortalState, updateTeeOnPortal } = useTeeOnPortalContext();
  const [loginState, setLoginState] = useState<ILoginState>({
    email: "",
    password: "",
  });
  const facilityShortName = new URLSearchParams(window.location.search)?.get("facility");

  function navigate(url: string, newTab = false) {
    if (newTab) {
      window.open(url);
    } else {
      history.push(url);
    }
  }

  async function handleLogin() {
    const params = {
      email: loginState.email,
      password: loginState.password,
    };

    const loginRes = await UserLogin(params, true);

    if (loginRes.status !== StatusCode.OK) {
      dispatch(showError("Invalid Credentials, please try again"));
      return null;
    }

    return loginRes.data?.user;
  }

  async function handleTeeOnPortalLogin() {
    const user = await handleLogin();
    if (!user) {
      return;
    }
    updateTeeOnPortal({ user: user });

    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const date = urlParams.get("date");
    const teeTimeId = urlParams.get("id");
    const facilityShortName = urlParams.get("facility");

    if (date && teeTimeId && facilityShortName) {
      await handleNavigateToTeeTime(date, Number(teeTimeId), facilityShortName);
    } else {
      const locationState = location.state as ILocationState;
      if (locationState?.params?.golfCourses) {
        void navigate(`/booking/teetimes/golf-courses`);
        return;
      }
      void navigate(`/booking/teetimes${facilityShortName ? "/".concat(facilityShortName) : ""}`);
    }
  }

  async function handleNavigateToTeeTime(teeTimeDate: string, teeTimeId: number, facilityShortName: string) {
    const facility =
      teeOnPortalState?.selectedTeeTime?.facility ??
      teeOnPortalState?.selectedFacilities?.find(facility => facility?.short_name === facilityShortName);
    let selectedTeeTime: ITeeTime & { formattedDate?: string; formattedStartTime?: string } =
      teeOnPortalState?.selectedTeeTime?.teeTime;

    //Load tee time if not already in state
    if (!selectedTeeTime && facility) {
      const teeTimeParams = {
        date: teeTimeDate,
        id: teeTimeId,
        facility_id: facility?.id,
        extended: true,
        turn_tee_time: true,
      };
      const teeTimeRes = await GetTeeTimesCustomer(teeTimeParams, true);
      if (teeTimeRes?.status !== StatusCode.OK) {
        dispatch(showError("Error getting selected tee time"));
        return;
      }
      if (teeTimeRes?.data?.length === 0) {
        dispatch(showError("Tee time is unavailable, please choose another"));
        history.push(`/booking/teetimes/${facilityShortName}`);
        return;
      }
      selectedTeeTime = teeTimeRes?.data[0];
    }

    const formattedStartTime = convertTime(selectedTeeTime?.start_time);
    const formattedDate = formatDateFromString(selectedTeeTime?.date);
    selectedTeeTime = { ...selectedTeeTime, formattedDate, formattedStartTime };

    if (selectedTeeTime?.id === teeTimeId) {
      //If not null - User has access to their own locked teetime
      if (selectedTeeTime?.lock !== null) {
        const tempCurrentDate = new Date().toUTCString();
        const currentTempTime = new Date(tempCurrentDate);
        const currentTime = new Date(currentTempTime).getTime();

        const tempString = String(selectedTeeTime?.lock?.start_time) + "Z";

        const lockStartDate = new Date(tempString);

        const tempExpiryString = String(selectedTeeTime?.lock?.expire_time) + "Z";
        const lockoutTempExpiry = new Date(tempExpiryString);

        const lockStartTime = lockStartDate.getTime();
        const lockoutExpiryTime = lockoutTempExpiry.getTime();

        const lockoutDuration = lockoutExpiryTime - currentTime;
        updateTeeOnPortal({
          selectedTeeTime: {
            ...teeOnPortalState?.selectedTeeTime,
            teeTime: selectedTeeTime,
            lock: { lockoutDuration, lockStartTime, expireTime: selectedTeeTime?.lock?.expire_time },
          },
        });
        history.push(`/booking/teetimes/book?id=${teeTimeId}&date=${teeTimeDate}`);
      } else {
        const bookingLockRes = await LockTeeTimeById({ tee_time_id: selectedTeeTime?.id ?? teeTimeId }, true);

        if (bookingLockRes.status !== StatusCode.OK) {
          dispatch(showError("Error locking tee time"));
          return;
        }

        //Locked out message
        const bookingLockMessage = "Tee time already locked by user";
        const bookingLockResMessage = bookingLockRes.data.message;

        if (bookingLockMessage === bookingLockResMessage) {
          dispatch(showError("Booking is locked by another user, please try again in a few minutes.."));
          return;
        }

        const tempCurrentDate = new Date().toUTCString();
        const currentTempTime = new Date(tempCurrentDate);
        const currentTime = new Date(currentTempTime).getTime();

        const tempString = String(bookingLockRes?.data?.start_time) + "Z";
        const lockStartDate = new Date(tempString);

        const tempExpiryString = String(bookingLockRes?.data?.expire_time) + "Z";
        const lockoutTempExpiry = new Date(tempExpiryString);

        const lockStartTime = lockStartDate.getTime();
        const lockoutExpiryTime = lockoutTempExpiry.getTime();

        const lockoutDuration = lockoutExpiryTime - currentTime;

        updateTeeOnPortal({
          selectedTeeTime: {
            ...teeOnPortalState?.selectedTeeTime,
            teeTime: selectedTeeTime,
            lock: { lockoutDuration, lockStartTime, expireTime: bookingLockRes?.data?.expire_time },
          },
        });
        history.push(`/booking/teetimes/book?id=${teeTimeId}&date=${teeTimeDate}`);
      }
    } else {
      dispatch(showError("Tee time is unavailable, please choose another"));
      history.push(`/booking/teetimes/${facilityShortName}`);
      return;
    }
  }

  function handleEnterKeydown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.code === "Enter" || e.code === "NumpadEnter") {
      void handleTeeOnPortalLogin();
    }
  }

  function navigateToRegister() {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const date = urlParams.get("date");
    const teeTimeId = urlParams.get("id");
    const facilityShortName = urlParams.get("facility");
    if (date && teeTimeId && facilityShortName) {
      void navigate(`/booking/register?id=${teeTimeId}&date=${date}&facility=${facilityShortName}`);
    } else {
      void navigate("/booking/register");
    }
  }

  return (
    <div className="tee-on-portal-login">
      <div className="tee-on-portal-login-top-bar">
        <div className="tee-on-portal-login-logo-container">
          <img
            className="tee-on-portal-nav-logo"
            src={"/public/images/Tee-On.png"}
            alt="Tee On Logo"
            onClick={() => navigate(`/booking/teetimes${facilityShortName ? "/".concat(facilityShortName) : ""}`)}
          />
        </div>

        <div>
          <span className="tee-on-portal-login-secondary-text">Don&apos;t have an account? </span>
          <span onClick={navigateToRegister} className="tee-on-portal-login-link-text">
            Sign Up
          </span>
        </div>
      </div>

      <div className="tee-on-portal-login-main-container-outer">
        <div className="tee-on-portal-login-main-container">
          <div className="tee-on-portal-login-text-group">
            <h1 className="tee-on-portal-login-primary-text">Log in to Your Account</h1>
            <p className="tee-on-portal-login-secondary-text">Welcome back! Please enter your details</p>
          </div>
          <FormLayout>
            <FormLayout.Group>
              <Input
                label="Email"
                value={loginState?.email}
                onChange={e => setLoginState(prevState => ({ ...prevState, email: e.target.value }))}
              />
            </FormLayout.Group>
            <FormLayout.Group>
              <Input.Password
                label={t("Password")}
                value={loginState.password}
                name="password"
                onKeyDown={handleEnterKeydown}
                onChange={e => setLoginState(prevState => ({ ...prevState, password: e.target.value }))}
              />
            </FormLayout.Group>
            <FormLayout.Group>
              <p onClick={() => navigate("/tee-on/portal/forgot", true)} className="tee-on-portal-login-link-text">
                Forgot Password
              </p>
            </FormLayout.Group>
            <FormLayout.Group>
              <Button
                disabled={!loginState?.email || !loginState?.password}
                type="primary"
                size="medium"
                onClick={handleTeeOnPortalLogin}
                block
              >
                Sign In
              </Button>
            </FormLayout.Group>
            <FormLayout.Group>
              <Button type="secondary" size="medium" onClick={navigateToRegister} block>
                Register
              </Button>
            </FormLayout.Group>
          </FormLayout>
        </div>
      </div>
    </div>
  );
}
